import { Fragment, useState, useEffect } from "react";
import { DMConfirmModal } from "../../../shared/DMModal/DMConfirmModal";
import Button from "../../../student/components/generic/button";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { deltamathAPI } from "../../utils";
import { useActivationContext } from "./LicenseActivationContext";
import { useDeltaToastContext } from "../../../shared/contexts/ToasterContext";
import { doNotMigrate } from "../../utils/quoteUtils";
import clsx from "clsx";

interface IMigrateOptions {
  copyLicense: boolean;
}

const MigrateSchool: React.FC<{ currentSchoolID: string }> = ({
  currentSchoolID,
}: {
  currentSchoolID: string;
}) => {
  const activationContext = useActivationContext();
  const queryClient = useQueryClient();
  const toastContext = useDeltaToastContext();
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [useCurrentSchoolAs, setUseCurrentSchoolAs] = useState<
    "source" | "target"
  >("source");
  const [otherSchool, setOtherSchool] = useState<string>("");
  const [options, setOptions] = useState<IMigrateOptions>({
    copyLicense: false,
  });
  const [useTargetNCES, setUseTargetNCES] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);

  const { data, refetch } = useQuery({
    queryKey: ["otherSchool"],
    queryFn: async () => {
      const response = await axios.get(
        `${deltamathAPI()}/manager_new/invoices/details/${
          activationContext.invoiceDetails?.quoteNumber
        }/school?entityType=school&id=${otherSchool}`
      );
      return response.data;
    },
    enabled: false,
  });

  const { mutate: migrateSchool } = useMutation({
    mutationFn: async () => {
      console.log({
        [useCurrentSchoolAs]: currentSchoolID,
        [useCurrentSchoolAs === "source" ? "target" : "source"]: otherSchool,
        options,
      });
      const response = await axios.post(
        `${deltamathAPI()}/manager_new/invoices/migrate_school/${
          activationContext.invoiceDetails?.quoteNumber
        }`,
        {
          [useCurrentSchoolAs]: currentSchoolID,
          [useCurrentSchoolAs === "source" ? "target" : "source"]: otherSchool,
          options,
          useTargetNCES,
        }
      );
      return response;
    },
    onSuccess: () => {
      toastContext.addToast({
        status: "Success",
        message: `Migration successful from ${
          useCurrentSchoolAs === "source" ? currentSchoolID : otherSchool
        } to ${
          useCurrentSchoolAs === "source" ? otherSchool : currentSchoolID
        }.`,
      });
    },
    onError: (error) => {
      toastContext.addToast({
        status: "Error",
        message: `Migration failed: ${
          axios.isAxiosError(error)
            ? error.response?.data?.message || "An error occurred"
            : JSON.stringify(error)
        }`,
      });
    },
    onSettled: () => {
      setShowMigrationModal(false);
      queryClient.removeQueries({ queryKey: ["otherSchool"] });
      queryClient.invalidateQueries({
        queryKey: ["invoice", activationContext.state.quoteNumber],
      });
    },
  });

  const handleSubmit = () => {
    if (!otherSchool) {
      toastContext.addToast({
        status: "Error",
        message: "Please enter a valid school ID to migrate.",
      });
      return;
    }
    migrateSchool();
  };

  useEffect(() => {
    if (
      doNotMigrate.includes(currentSchoolID) ||
      doNotMigrate.includes(otherSchool)
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [currentSchoolID, otherSchool]);

  return (
    <Fragment>
      <Button className="mt-2" onClick={() => setShowMigrationModal(true)}>
        Migrate
      </Button>
      <DMConfirmModal
        title="Migrate School"
        confirmText="Migrate"
        cancelText="Cancel"
        visible={showMigrationModal}
        confirmButtonProps={{ disabled: disabled }}
        onConfirm={handleSubmit}
        onCancel={() => {
          setShowMigrationModal(false);
          queryClient.removeQueries({ queryKey: ["otherSchool"] });
        }}
      >
        <h1 className="text-lg">
          Copy data from the source school - other than school id - into the
          target school id.
        </h1>
        <div className="flex flex-col gap-4">
          <div className="flex flex-row gap-2">
            <div className="flex flex-1 flex-col">
              <label htmlFor="currentSchool" className="mb-1 font-medium">
                Current School NCES
              </label>
              <input
                id="currentSchool"
                type="text"
                className="border-none px-2 hover:cursor-default"
                value={currentSchoolID}
                readOnly
              />
            </div>
            <div className="flex flex-1 flex-col">
              <label htmlFor="currentSchool" className="mb-1 font-medium">
                Use As
              </label>
              <select
                id="useAs"
                value={useCurrentSchoolAs}
                onChange={(e) =>
                  setUseCurrentSchoolAs(e.target.value as "source" | "target")
                }
              >
                <option value="source">Source</option>
                <option value="target">Target</option>
              </select>
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex flex-1 flex-col">
              <label htmlFor="currentSchool" className="mb-1 font-medium">
                {useCurrentSchoolAs === "source" ? "Target" : "Source"} School
                NCES
              </label>
              <div className="flex flex-row gap-2">
                <input
                  id="currentSchool"
                  type="text"
                  className="rounded border px-2"
                  value={otherSchool}
                  onChange={(e) => setOtherSchool(e.target.value)}
                />
                <Button
                  className="text-sm"
                  onClick={() => refetch()}
                  submit={false}
                >
                  Find Details
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p>
              <strong>Migration Options:</strong>
            </p>
            <div className="ml-4 flex flex-col">
              <div className="flex flex-row items-center gap-2">
                <label>Copy Source License to Target:</label>
                <input
                  type="checkbox"
                  checked={options.copyLicense}
                  onChange={(e) =>
                    setOptions((prev) => ({
                      ...prev,
                      copyLicense: e.target.checked,
                    }))
                  }
                />
              </div>
            </div>
          </div>
          {useCurrentSchoolAs === "source" && (
            <div className="flex flex-row items-center gap-2">
              <p>
                <strong>Use Target NCES for this quote?</strong>
              </p>
              <input
                type="checkbox"
                checked={useTargetNCES}
                onChange={(e) => setUseTargetNCES(e.target.checked)}
              />
            </div>
          )}
        </div>
        {data?.siteInfo && (
          <div className="mt-4 flex flex-col gap-2" key="school-details">
            <h3 className="font-semibold">School Details</h3>
            <p key="school-name">
              <strong>School Name:</strong> {data.siteInfo.schoolName}
            </p>
            {data.siteInfo.district.districtName && (
              <p key="district-name">
                <strong>District:</strong> {data.siteInfo.district.districtName}
              </p>
            )}
            <p key="license-expiration">
              <strong>School License Expiration:</strong>{" "}
              {data.siteInfo.dmLicense?.expiration || "N/A"}
            </p>
          </div>
        )}
        {disabled && (
          <div className="mt-4 flex flex-col gap-2" key="school-details">
            <h3 className="font-semibold">Cannot Migrate</h3>
            <p>
              One of these sites cannot be used as the source or target of a
              migration.
            </p>
          </div>
        )}
      </DMConfirmModal>
    </Fragment>
  );
};

export default MigrateSchool;
